import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

// Fallback SVG component for when images fail to load
const FallbackPreview = ({ template }) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 600 800"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ background: "white" }}
  >
    <rect width="600" height="800" fill="#FFFFFF" />
    <rect
      x="40"
      y="40"
      width="520"
      height="80"
      fill={template.previewData.accentColor}
    />
    <rect x="40" y="140" width="520" height="40" fill="#F3F4F6" />
    <rect x="40" y="200" width="520" height="200" fill="#F3F4F6" />
    <rect x="40" y="420" width="520" height="160" fill="#F3F4F6" />
    <rect x="40" y="600" width="520" height="160" fill="#F3F4F6" />
    <text
      x="300"
      y="85"
      fill="white"
      fontSize="24"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {template.name}
    </text>
  </svg>
);

const categories = [
  { id: "all", name: "All Templates" },
  // { id: "simple", name: "Simple" },
  // { id: "professional", name: "Professional" },
  // { id: "modern", name: "Modern" },
  // { id: "creative", name: "Creative" },
];

const TemplateSelector = ({ templates }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [failedImages, setFailedImages] = useState(new Set());

  const filteredTemplates = templates.filter(
    (template) =>
      selectedCategory === "all" || template.category === selectedCategory
  );

  const handleImageError = (templateId) => {
    setFailedImages((prev) => new Set([...prev, templateId]));
  };

  return (
    <div className="bg-background py-16">
      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={selectedCategory === category.id ? "default" : "outline"}
            onClick={() => setSelectedCategory(category.id)}
            className="min-w-[120px] cursor-pointer hover:scale-105 transition-transform"
          >
            {category.name}
          </Button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 container mx-auto px-4">
        {filteredTemplates.map((template) => (
          <Link
            to={`/editor/${template.id}`}
            key={template.id}
            className="block transform transition-all duration-300 hover:-translate-y-1 cursor-pointer no-underline text-inherit"
          >
            <Card className="overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
              {/* Template Preview Image */}
              <div className="relative aspect-[3/4] overflow-hidden bg-white">
                {failedImages.has(template.id) ? (
                  <FallbackPreview template={template} />
                ) : (
                  <img
                    src={template.imageUrl}
                    alt={`${template.name} template preview`}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    onError={() => handleImageError(template.id)}
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="bg-white text-black px-4 py-2 rounded-md font-medium">
                    Use Template
                  </span>
                </div>
              </div>

              {/* Template Info */}
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h3 className="font-medium text-xl text-gray-900">
                    {template.name}
                  </h3>
                  <span
                    className="px-2 py-1 text-xs rounded-full capitalize"
                    style={{
                      backgroundColor: `${template.previewData.accentColor}15`,
                      color: template.previewData.accentColor,
                    }}
                  >
                    {template.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {template.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {template.previewData.features.map((feature, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full"
                    >
                      {feature.replace(/-/g, " ")}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default TemplateSelector;
